import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Progress } from "../components/ui/progress";
import { 
  Activity, TrendingDown, TrendingUp, AlertTriangle, Heart, 
  Brain, Pill, Calendar, RefreshCw, Users, FileText, Video,
  ThermometerSun, Droplet, Moon, Footprints, Target, Zap,
  BellRing, CheckCircle2, Clock, ChevronRight
} from 'lucide-react';
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

// Stability Score thresholds
const STABILITY_THRESHOLDS = {
  stable: { min: 70, color: 'green', label: 'Stable' },
  monitor: { min: 50, color: 'yellow', label: 'Monitor Closely' },
  concern: { min: 30, color: 'orange', label: 'Proactive Intervention' },
  critical: { min: 0, color: 'red', label: 'Immediate Attention' }
};

const StabilityScoreDashboard = () => {
  const [dashboard, setDashboard] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [patientAnalysis, setPatientAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const { toast } = useToast();

  const token = localStorage.getItem('token');

  const fetchDashboard = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/phase38-44/predictive/dashboard`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setDashboard(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Analyze patient's decline trajectory
  const analyzePatient = async (patientId) => {
    try {
      const res = await fetch(`${API}/api/phase38-44/predictive/decline-trajectory/${patientId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPatientAnalysis(data);
        setActiveTab('analysis');
      }
    } catch (err) {
      toast({ title: "Error", description: "Failed to analyze patient", variant: "destructive" });
    }
  };

  // Calculate stability score color
  const getScoreColor = (score) => {
    if (score >= 70) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    if (score >= 30) return 'text-orange-600';
    return 'text-red-600';
  };

  const getScoreBackground = (score) => {
    if (score >= 70) return 'bg-green-100';
    if (score >= 50) return 'bg-yellow-100';
    if (score >= 30) return 'bg-orange-100';
    return 'bg-red-100';
  };

  const getScoreLabel = (score) => {
    if (score >= 70) return 'Stable';
    if (score >= 50) return 'Monitor Closely';
    if (score >= 30) return 'Proactive Intervention';
    return 'Immediate Attention';
  };

  // Trigger Automatic Triage Alert
  const triggerTriageAlert = async (patientId, patientName, analysis) => {
    toast({
      title: "Automatic Triage Alert",
      description: `Stability score dropped below threshold for ${patientName}. Nurse notified.`,
      action: (
        <Button variant="outline" size="sm" onClick={() => {
          // Suggest immediate telehealth
          toast({ title: "Telehealth Scheduled", description: "A check-in call has been initiated." });
        }}>
          Start Telehealth
        </Button>
      ),
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Activity className="h-7 w-7 text-teal-600" />
            Stability Score Dashboard
          </h1>
          <p className="text-gray-600">Predictive analytics for proactive comfort care</p>
        </div>
        <Button variant="outline" onClick={fetchDashboard}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh Data
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-red-600 font-medium">Critical</p>
                <p className="text-3xl font-bold text-red-700">
                  {dashboard?.summary?.decline_alerts_7d || 0}
                </p>
              </div>
              <AlertTriangle className="h-10 w-10 text-red-500" />
            </div>
            <p className="text-xs text-red-600 mt-2">Decline alerts (7 days)</p>
          </CardContent>
        </Card>

        <Card className="bg-orange-50 border-orange-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-orange-600 font-medium">Burnout Flags</p>
                <p className="text-3xl font-bold text-orange-700">
                  {dashboard?.summary?.burnout_alerts_7d || 0}
                </p>
              </div>
              <Brain className="h-10 w-10 text-orange-500" />
            </div>
            <p className="text-xs text-orange-600 mt-2">Caregiver burnout detected</p>
          </CardContent>
        </Card>

        <Card className="bg-yellow-50 border-yellow-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-yellow-600 font-medium">Pain Escalation</p>
                <p className="text-3xl font-bold text-yellow-700">
                  {dashboard?.summary?.pain_alerts_7d || 0}
                </p>
              </div>
              <Heart className="h-10 w-10 text-yellow-500" />
            </div>
            <p className="text-xs text-yellow-600 mt-2">Pain alerts (7 days)</p>
          </CardContent>
        </Card>

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-600 font-medium">Unresolved</p>
                <p className="text-3xl font-bold text-blue-700">
                  {(dashboard?.summary?.unresolved_decline || 0) + 
                   (dashboard?.summary?.unresolved_burnout || 0)}
                </p>
              </div>
              <Clock className="h-10 w-10 text-blue-500" />
            </div>
            <p className="text-xs text-blue-600 mt-2">Pending action</p>
          </CardContent>
        </Card>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="alerts">Recent Alerts</TabsTrigger>
          <TabsTrigger value="analysis">Patient Analysis</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          {/* How Stability Score Works */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Target className="h-5 w-5 text-teal-600" />
                Stability Score Algorithm
              </CardTitle>
              <CardDescription>
                Aggregating data from multiple sources to predict patient needs
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <FileText className="h-8 w-8 mx-auto text-teal-600 mb-2" />
                  <h4 className="font-medium">Caregiver Logs</h4>
                  <p className="text-xs text-gray-500 mt-1">Symptom reports, task completion</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <Activity className="h-8 w-8 mx-auto text-blue-600 mb-2" />
                  <h4 className="font-medium">IoT Vitals</h4>
                  <p className="text-xs text-gray-500 mt-1">Heart rate, O2, activity levels</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <Pill className="h-8 w-8 mx-auto text-purple-600 mb-2" />
                  <h4 className="font-medium">Medication Refills</h4>
                  <p className="text-xs text-gray-500 mt-1">Compliance and frequency</p>
                </div>
                <div className="p-4 bg-gray-50 rounded-lg text-center">
                  <Brain className="h-8 w-8 mx-auto text-orange-600 mb-2" />
                  <h4 className="font-medium">NLP Sentiment</h4>
                  <p className="text-xs text-gray-500 mt-1">Message tone analysis</p>
                </div>
              </div>

              {/* Score Interpretation */}
              <div className="mt-6">
                <h4 className="font-medium mb-3">Score Interpretation</h4>
                <div className="space-y-2">
                  {Object.entries(STABILITY_THRESHOLDS).map(([key, { min, color, label }]) => (
                    <div key={key} className="flex items-center gap-3">
                      <div className={`w-4 h-4 rounded-full bg-${color}-500`} 
                           style={{ backgroundColor: 
                             color === 'green' ? '#22c55e' :
                             color === 'yellow' ? '#eab308' :
                             color === 'orange' ? '#f97316' : '#ef4444'
                           }} 
                      />
                      <span className="font-medium">{label}</span>
                      <span className="text-sm text-gray-500">
                        ({min === 0 ? '< 30' : `${min}+`})
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Automatic Triage Workflow */}
          <Card className="bg-gradient-to-r from-teal-50 to-cyan-50 border-teal-200">
            <CardHeader>
              <CardTitle className="text-lg text-teal-900 flex items-center gap-2">
                <Zap className="h-5 w-5" />
                Automatic Triage Protocol
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="text-center">
                    <div className="bg-white p-3 rounded-full border-2 border-teal-300">
                      <TrendingDown className="h-6 w-6 text-teal-600" />
                    </div>
                    <p className="text-xs mt-1">Score Drops</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-teal-400" />
                  <div className="text-center">
                    <div className="bg-white p-3 rounded-full border-2 border-teal-300">
                      <BellRing className="h-6 w-6 text-teal-600" />
                    </div>
                    <p className="text-xs mt-1">Alert Nurse</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-teal-400" />
                  <div className="text-center">
                    <div className="bg-white p-3 rounded-full border-2 border-teal-300">
                      <Video className="h-6 w-6 text-teal-600" />
                    </div>
                    <p className="text-xs mt-1">Suggest Telehealth</p>
                  </div>
                  <ChevronRight className="h-5 w-5 text-teal-400" />
                  <div className="text-center">
                    <div className="bg-white p-3 rounded-full border-2 border-teal-300">
                      <Heart className="h-6 w-6 text-teal-600" />
                    </div>
                    <p className="text-xs mt-1">Comfort Care</p>
                  </div>
                </div>
                <div className="text-center md:text-right">
                  <p className="text-sm text-teal-700 font-medium">Goal:</p>
                  <p className="text-lg text-teal-900 font-bold">Proactive Comfort Care</p>
                  <p className="text-xs text-teal-600">Not reactive crisis management</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Alerts Tab */}
        <TabsContent value="alerts" className="space-y-4">
          {/* Recent Decline Alerts */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <TrendingDown className="h-5 w-5 text-red-500" />
                Recent Decline Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.recent_decline_alerts?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.recent_decline_alerts.map((alert, i) => (
                    <div key={i} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-full ${
                          alert.priority === 'high' ? 'bg-red-100' : 'bg-yellow-100'
                        }`}>
                          <AlertTriangle className={`h-5 w-5 ${
                            alert.priority === 'high' ? 'text-red-600' : 'text-yellow-600'
                          }`} />
                        </div>
                        <div>
                          <h4 className="font-medium">{alert.patient_name}</h4>
                          <p className="text-sm text-gray-600">{alert.title}</p>
                          <p className="text-xs text-gray-500">
                            {new Date(alert.created_at).toLocaleString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={
                          alert.status === 'unread' ? 'bg-red-500' : 'bg-gray-400'
                        }>
                          {alert.status}
                        </Badge>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => analyzePatient(alert.patient_id)}
                        >
                          Analyze
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-6">No recent decline alerts</p>
              )}
            </CardContent>
          </Card>

          {/* Caregiver Burnout Alerts */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Brain className="h-5 w-5 text-orange-500" />
                Caregiver Burnout Alerts
              </CardTitle>
            </CardHeader>
            <CardContent>
              {dashboard?.recent_burnout_alerts?.length > 0 ? (
                <div className="space-y-3">
                  {dashboard.recent_burnout_alerts.map((alert, i) => (
                    <div key={i} className="flex items-center justify-between p-3 border rounded-lg bg-orange-50">
                      <div className="flex items-center gap-3">
                        <Brain className="h-5 w-5 text-orange-600" />
                        <div>
                          <h4 className="font-medium">{alert.patient_name}</h4>
                          <p className="text-sm text-gray-600">{alert.message}</p>
                        </div>
                      </div>
                      <Badge className="bg-orange-500">Social Worker Outreach</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500 py-6">No recent burnout alerts</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Patient Analysis Tab */}
        <TabsContent value="analysis" className="space-y-4">
          {patientAnalysis ? (
            <>
              {/* Stability Score Display */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Patient Stability Analysis</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col md:flex-row items-center gap-6">
                    {/* Score Circle */}
                    <div className={`relative w-40 h-40 ${getScoreBackground(100 - patientAnalysis.risk_score)} rounded-full flex items-center justify-center`}>
                      <div className="text-center">
                        <p className={`text-4xl font-bold ${getScoreColor(100 - patientAnalysis.risk_score)}`}>
                          {100 - patientAnalysis.risk_score}
                        </p>
                        <p className="text-sm text-gray-600">Stability Score</p>
                      </div>
                    </div>

                    <div className="flex-1">
                      <h3 className={`text-xl font-semibold ${getScoreColor(100 - patientAnalysis.risk_score)}`}>
                        {getScoreLabel(100 - patientAnalysis.risk_score)}
                      </h3>
                      <p className="text-gray-600 mt-1">{patientAnalysis.recommendation}</p>
                      
                      <div className="mt-4 grid grid-cols-2 gap-3">
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <p className="text-lg font-bold">{patientAnalysis.data_points?.assessments || 0}</p>
                          <p className="text-xs text-gray-500">Assessments</p>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <p className="text-lg font-bold">{patientAnalysis.data_points?.vitals || 0}</p>
                          <p className="text-xs text-gray-500">Vitals</p>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <p className="text-lg font-bold">{patientAnalysis.data_points?.activities || 0}</p>
                          <p className="text-xs text-gray-500">Activities</p>
                        </div>
                        <div className="text-center p-2 bg-gray-50 rounded">
                          <p className="text-lg font-bold">{patientAnalysis.data_points?.symptoms || 0}</p>
                          <p className="text-xs text-gray-500">Symptoms</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Risk Factors */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Contributing Risk Factors</CardTitle>
                </CardHeader>
                <CardContent>
                  {patientAnalysis.risk_factors?.length > 0 ? (
                    <div className="space-y-3">
                      {patientAnalysis.risk_factors.map((factor, i) => (
                        <div key={i} className={`p-3 rounded-lg border-l-4 ${
                          factor.severity === 'high' ? 'border-red-500 bg-red-50' :
                          factor.severity === 'moderate' ? 'border-orange-500 bg-orange-50' :
                          'border-yellow-500 bg-yellow-50'
                        }`}>
                          <div className="flex items-center justify-between">
                            <h4 className="font-medium">{factor.factor}</h4>
                            <Badge variant="outline" className={
                              factor.severity === 'high' ? 'text-red-600' :
                              factor.severity === 'moderate' ? 'text-orange-600' : 'text-yellow-600'
                            }>
                              {factor.severity}
                            </Badge>
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{factor.description}</p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-6">
                      <CheckCircle2 className="h-12 w-12 mx-auto text-green-500 mb-2" />
                      <p className="text-green-700 font-medium">No significant risk factors identified</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </>
          ) : (
            <Card>
              <CardContent className="p-8 text-center">
                <Activity className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <h3 className="font-semibold text-gray-600">Select a Patient</h3>
                <p className="text-sm text-gray-500">
                  Choose a patient from the alerts list or enter a patient ID to analyze their stability score.
                </p>
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StabilityScoreDashboard;
